import { HIGHLIGHT_FILL_HANDLE_CLASS } from '@databases/highlights/constants';
import { type MouseHitTarget, type MouseInputAdapter } from '@databases/selections';

import { isPointerOnDatabaseScrollableNativeScrollbar } from './native-scrollbar-hit.ts';

type HitTargetResolver = (
  element: Element,
  clientX: number,
  clientY: number,
) => MouseHitTarget | null;
type GridCoord = Extract<MouseHitTarget, { type: 'row' }>['row'];

const HEADER_CHECKBOX_SELECTOR = '#selections-header-checkbox';
const ROW_BUTTON_SELECTOR = '[data-testid="row-select-button"]';
const ROW_CHECKBOX_SELECTOR = '.selections-entry-checkbox';
const FILL_HANDLE_SELECTOR = `.${HIGHLIGHT_FILL_HANDLE_CLASS}`;
const SCROLLABLE_INNER_CONTAINER_ID = 'scrollable-inner-container';
const PINNED_LEADING_BOUNDARY_SELECTOR =
  '[data-dbs-pinned-leading-freeze-boundary="true"]';
const DATABASE_BOE_PROMPT_INPUT_SELECTOR =
  '[data-testid="database-blank-object-experience"] [contenteditable]:not([contenteditable="false"]), [data-testid="database-blank-object-experience"] .ProseMirror';
function parseGridCoord(value: string | null): GridCoord | null {
  if (value === null || !/^\d+$/.test(value)) {
    return null;
  }

  const index = Number.parseInt(value, 10);

  return Number.isSafeInteger(index) ? { index } : null;
}

const resolveHeaderCheckbox: HitTargetResolver = (element) =>
  element.closest(HEADER_CHECKBOX_SELECTOR) ? { type: 'headerCheckbox' } : null;

const resolveRowButton: HitTargetResolver = (element) => {
  const rowSelectButton = element.closest(ROW_BUTTON_SELECTOR);

  if (!rowSelectButton) {
    return null;
  }

  const row = parseGridCoord(
    rowSelectButton.closest('tr[data-row-index]')?.getAttribute('data-row-index') ?? null,
  );

  return row ? { type: 'row', row, source: 'row-handle' } : { type: 'none' };
};

const resolveRowCheckbox: HitTargetResolver = (element) => {
  const rowCheckbox = element.closest(ROW_CHECKBOX_SELECTOR);

  if (!rowCheckbox) {
    return null;
  }

  const row = parseGridCoord(rowCheckbox.getAttribute('data-row-index'));

  return row ? { type: 'row', row, source: 'row-checkbox' } : { type: 'none' };
};

const resolveCell: HitTargetResolver = (element) => {
  const cell = element.closest('[data-row-index][data-col-index]');

  if (!cell) {
    return null;
  }

  const row = parseGridCoord(cell.getAttribute('data-row-index'));
  const col = parseGridCoord(cell.getAttribute('data-col-index'));

  if (!row || !col) {
    return { type: 'none' };
  }

  return {
    type: 'cell',
    horizontalAutoScroll:
      cell.getAttribute('data-dbs-pinned-leading') === 'true' ? 'disabled' : undefined,
    position: { row, col },
  };
};

const resolveFillHandle: HitTargetResolver = (element) =>
  element.closest(FILL_HANDLE_SELECTOR) ? { type: 'fillHandle' } : null;

const resolveOutside: HitTargetResolver = (element, clientX, clientY) => {
  const isInsideLayoutShell = element.closest(
    '#scrollable-inner-container, [data-testid="layout-view--wrapper"], #layout-container',
  );

  if (!isInsideLayoutShell) {
    return null;
  }

  const isInteractive = element.closest(
    'table, button, [role="button"], input, textarea, a, [data-testid="database-value-view-container"], [data-testid="layout-right-side-panel"]',
  );
  const isDatabaseBoePromptInput = element.closest(DATABASE_BOE_PROMPT_INPUT_SELECTOR);

  if (isInteractive || isDatabaseBoePromptInput) {
    return { type: 'none' };
  }

  return isPointerOnDatabaseScrollableNativeScrollbar(element, clientX, clientY)
    ? { type: 'none' }
    : { type: 'outside' };
};

const hitTargetResolvers: readonly HitTargetResolver[] = [
  resolveHeaderCheckbox,
  resolveRowButton,
  resolveRowCheckbox,
  resolveCell,
  resolveFillHandle,
  resolveOutside,
];

function hitTest(clientX: number, clientY: number): MouseHitTarget {
  const el = document.elementFromPoint(clientX, clientY);

  if (!el) {
    return { type: 'none' };
  }

  for (const resolver of hitTargetResolvers) {
    const target = resolver(el, clientX, clientY);

    if (target) {
      return target;
    }
  }

  return { type: 'none' };
}

/**
 * Adapts the current Databases layout DOM to the layout-neutral mouse input contract.
 * Selectors and geometry remain here so another renderer can provide semantic hit targets
 * without changing `@databases/selections`.
 */
export function createTableMouseInputAdapter(): MouseInputAdapter {
  return {
    hitTest,

    getScrollContainer() {
      return document.getElementById(SCROLLABLE_INNER_CONTAINER_ID);
    },

    getHorizontalAutoScrollViewportLeft() {
      const fixedBoundary = document
        .getElementById(SCROLLABLE_INNER_CONTAINER_ID)
        ?.querySelector(PINNED_LEADING_BOUNDARY_SELECTOR);

      return fixedBoundary?.getBoundingClientRect().right ?? null;
    },
  };
}
